import { articleMetaBySlug } from "./meta";

export const meta = articleMetaBySlug["how-many-training-zones"]!;

export function Body() {
  return (
    <>
      <p>
        Training zones come in many versions. Some coaches use three, others five, seven or nine.
        It is natural to think that more zones mean more precise training. Research on how
        intensity is measured points in a different direction.
      </p>

      <h2>What the researchers did</h2>
      <p>
        In 2020, a group of researchers from Australia and the United States reviewed the
        common ways of setting training intensity. They asked a simple question: does the
        method actually put different people in the same physiological state? For example, does
        running at a given percentage of maximum heart rate cause the same response in the body
        for two different runners?
      </p>

      <h2>What they found</h2>
      <p>
        Setting intensity as a fixed percentage of a maximum, such as maximum heart rate,
        maximal oxygen uptake or maximal speed, had little merit for that purpose. The same
        percentage can be easy for one runner and hard for another.
      </p>
      <p>
        Physiologically, the body has two important boundaries, which divide running into three
        intensity domains:
      </p>
      <ul>
        <li>
          Below the first threshold you can talk comfortably and keep going for a long time.
          This is where your easy running belongs.
        </li>
        <li>
          Between the first threshold and critical speed, running is comfortably hard. The body
          still settles into a steady state, but it costs more.
        </li>
        <li>
          Above critical speed, fatigue builds up steadily, and you can only keep going for a
          limited time. This is where hard intervals belong.
        </li>
      </ul>
      <p>
        The review found reasonable support for the first threshold as the lower boundary. For
        the upper boundary, critical speed and critical power were the best supported. A study
        of ten well-trained runners showed why the method matters: their critical speed was on
        average 16.4 km/h, while a lactate-based threshold measured on the same runners was 15.2
        km/h. Different tests give different "thresholds".
      </p>

      <h2>What it means for you</h2>
      <p>
        Your body has two boundaries, not five, seven or nine. Zone systems with more steps can
        still be practical for describing sessions, but the boundaries between the steps are
        conventions, not measurements. More zones do not make your training more precise if the
        reference behind them is an estimate.
      </p>
      <p>
        What matters more is that a session has a clear purpose and a target you can actually
        follow. On easy days, the talk test is often a better guide than any zone.
      </p>

      <h2>What the study can't tell you</h2>
      <p>
        The review looked at how the body responds during exercise. The researchers point out
        that this does not automatically tell you which way of setting intensity leads to the
        best improvement over weeks and months. That needs training studies.
      </p>

      <h2>How we use it</h2>
      <p>
        PX7 Training used to work with seven zones. Today a session shows a pace or power target
        with a range, based on your own races, tests and runs. Easy runs have a ceiling, and you
        can always run slower. Behind the scenes, we describe intensity with the three
        physiological domains.
      </p>

      <h2>Sources</h2>
      <ul>
        <li>
          Jamnick et al. (2020), An examination and critique of current methods to determine
          exercise intensity.{" "}
          <a href="https://doi.org/10.1007/s40279-020-01322-8">Sports Medicine</a>.
        </li>
        <li>
          Nixon et al. (2021), Steady-state VO₂ above MLSS: evidence that critical speed better
          represents maximal metabolic steady state in well-trained runners.{" "}
          <a href="https://doi.org/10.1007/s00421-021-04780-8">European Journal of Applied Physiology</a>.
        </li>
      </ul>
    </>
  );
}
